import { HdChironPosition, HdChironSide } from './hd-chiron.models';
import { HD_ASTRO_POINT_DISPLAY_CONFIGS, HdAstroPointKey } from './planet-grid/hd-planet-display-config';

/**
 * Positie van een astrologisch punt (AC, DC, MC, IC, Black Moon Lilith) van één zijde, uit
 * `metadata.extraPlanets` van de berekening. BELANGRIJK: net als Chiron géén activatie: het punt
 * definieert geen poorten of kanalen en telt nergens mee. Enkel informatief (astro posities onder de
 * planetengrid, gate-info). Spec: docs/superpowers/specs/2026-10-06-astro-posities-design.md.
 */
export interface HdAstroPointPosition extends Omit<HdChironPosition, 'side'> {
  readonly key: HdAstroPointKey;
  readonly side: HdChironSide;
}

/** Klik op een cel van de astro-rij: Chiron of een astrologisch punt van één zijde. */
export interface HdAstroPointClickEvent {
  readonly key: HdAstroPointKey | 'chiron';
  readonly side: HdChironSide;
  readonly origin: { readonly x: number; readonly y: number };
}

function toFiniteNumber(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * De astrologische punten van één zijde, in de vaste rij-volgorde (AC, DC, MC, IC, Lilith). Punten
 * zonder (geldige) data vallen weg: zo hebben charts zonder geboortecoördinaten geen hoeken.
 */
export function resolveHdAstroPointPositions(
  metadata: Record<string, unknown> | null | undefined,
  side: HdChironSide,
): readonly HdAstroPointPosition[] {
  const extraPlanets = metadata?.['extraPlanets'];
  if (!Array.isArray(extraPlanets)) return [];
  const entries = extraPlanets as Array<Record<string, unknown>>;
  const positions: HdAstroPointPosition[] = [];
  for (const config of HD_ASTRO_POINT_DISPLAY_CONFIGS) {
    const entry = entries.find(candidate => candidate['name'] === config.backendName && candidate['side'] === side);
    if (!entry) continue;
    const gate = toFiniteNumber(entry['gate']);
    const line = toFiniteNumber(entry['line']);
    if (gate < 1 || gate > 64 || line < 1 || line > 6) continue;
    positions.push({
      key: config.key,
      side,
      gate,
      line,
      color: toFiniteNumber(entry['color']),
      tone: toFiniteNumber(entry['tone']),
      base: toFiniteNumber(entry['base']),
      longitude: toFiniteNumber(entry['longitude']),
    });
  }
  return positions;
}
